///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { NodeFeature } from '../entities/node/node-feature';
import { LinkFeature } from '../entities/link/link-feature';
import { SectorFeature } from '../entities/sector/sector-feature';
import { ProjectSelectionDTO } from '../../../generated/shared-types.model';

export class SelectionService {
    private readonly stations: Map<number, NodeFeature>;
    private readonly links: Map<number, LinkFeature>;
    private readonly sectors: Map<number, SectorFeature>;

    constructor() {
        this.stations = new Map();
        this.links = new Map();
        this.sectors = new Map();
    }

    clear(): void {
        this.stations.clear();
        this.links.clear();
        this.sectors.clear();
    }

    addStation(station: NodeFeature): void {
        if (this.stations.has(station.getId())) {
            this.removeStation(station.getId());
        } else {
            this.stations.set(station.getId(), station);
            station.select();
        }
    }

    addLink(link: LinkFeature): void {
        if (this.links.has(link.getId())) {
            this.removeLink(link.getId());
        } else {
            this.links.set(link.getId(), link);
            link.select();
        }
    }

    addSector(sector: SectorFeature): void {
        if (this.sectors.has(sector.generateAggregatedSectorIndex())) {
            this.clearSectorSelection();
        } else {
            this.sectors.set(sector.generateAggregatedSectorIndex(), sector);
        }
    }

    removeStation(stationId: number): void {
        if (this.stations.get(stationId)) {
            this.stations.get(stationId).unselect();
        }
        this.stations.delete(stationId);
    }

    removeLink(linkId: number): void {
        if (this.links.get(linkId)) {
            this.links.get(linkId).unselect();
        }
        this.links.delete(linkId);
    }

    removeSector(aggregatedSectorIndex: number): void {
        this.sectors.delete(aggregatedSectorIndex);
    }

    hasStation(stationId: number): boolean {
        return this.stations.has(stationId);
    }

    hasSelectedLinks(): boolean {
        return this.links && this.links.size > 0;
    }

    hasSelectedStations(): boolean {
        return this.stations && this.stations.size > 0;
    }

    getSelectedEntities(): ProjectSelectionDTO {
        const selectionDTO = {
            selectedNodes: [],
            selectedLinkIds: [],
            selectedSectors: []
        } as ProjectSelectionDTO;

        for (const station of this.getStations()) {
            selectionDTO.selectedNodes.push(station.getName());
        }

        for (const link of this.getLinks()) {
            selectionDTO.selectedLinkIds.push(link.getId());
        }

        for (const sectors of this.sectors.values()) {
            selectionDTO.selectedSectors.push(sectors.generateAggregatedSectorIndex());
        }

        return selectionDTO;
    }

    getStations(): IterableIterator<NodeFeature> {
        return this.stations.values();
    }

    getLinks(): IterableIterator<LinkFeature> {
        return this.links.values();
    }

    hasSelectedNotFrozenEntities(): boolean {
        for (const station of this.stations.values()) {
            if (!station.hasFreezeLinks()) {
                return true;
            }
        }
        for (const link of this.links.values()) {
            if (!link.isUserForced()) {
                return true;
            }
        }
        return false;
    }

    getSelectedNotFrozenLinks(): number[] {
        const ids = [];
        for (const links of this.links.values()) {
            if (!links.isUserForced()) {
                ids.push(links.getId());
            }
        }
        return ids;
    }

    getSelectedNotFrozenStations(): number[] {
        const ids = [];
        for (const station of this.stations.values()) {
            if (!station.hasFreezeLinks()) {
                ids.push(station.getId());
            }
        }
        return ids;
    }

    clearSelection(): void {
        for (const station of this.stations.values()) {
            station.unselect();
        }
        for (const link of this.links.values()) {
            link.unselect();
        }

        this.stations.clear();
        this.links.clear();
    }

    clearSectorSelection(): void {
        for (const sector of this.sectors.values()) {
            sector.unselect();
        }
        this.sectors.clear();
    }

    getStationsCount(): number {
        return this.stations.size;
    }

    getLinksCount(): number {
        return this.links.size;
    }
}
